import { style, styleVariants, keyframes, globalStyle } from '@vanilla-extract/css';
import { vars } from '../../theme/contract.css';
import { reducedMotion, mobile, reducedTransparency } from '../../theme/media';
import { fadeIn, fadeOut } from '../../keyframes';
import { keyboardInset } from '../../theme/layout.css';

// Phase 1 — Neon Quest 시각 언어를 시스템 표준 모달로 채택.
// default(cyan)만. motif별 색 swap은 Phase 2에서 추가.

const REST = 'translateY(0) scale(1)';

const riseIn = keyframes({
  '0%': { opacity: 0, transform: 'translateY(40px) scale(0.92)' },
  '70%': { opacity: 1, transform: 'translateY(-4px) scale(1.01)' },
  '100%': { opacity: 1, transform: REST },
});
const riseOut = keyframes({
  from: { opacity: 1, transform: REST },
  to: { opacity: 0, transform: 'translateY(30px) scale(0.94)' },
});

const neonPulse = keyframes({
  '0%, 100%': { filter: 'brightness(1)' },
  '50%': { filter: 'brightness(1.15)' },
});

const hudBlink = keyframes({
  '0%, 50%': { opacity: 1 },
  '51%, 100%': { opacity: 0.3 },
});

// ---------- Overlay ----------
export const overlay = style({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  // Why: position:fixed 라 MainLayout 셸 축소가 닿지 않는다 — 오버레이 하단을 직접 인셋만큼 올린다.
  bottom: keyboardInset,
  background: vars.shadow.overlay,
  backdropFilter: 'blur(4px) saturate(1.3)',
  WebkitBackdropFilter: 'blur(4px) saturate(1.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: vars.elevation.overlay,
  padding: vars.spacing.padding.xl,
  animation: `${fadeIn} ${vars.animation.duration.medium} ${vars.animation.easing.easeOut}`,
  '@media': {
    [reducedMotion]: { animation: 'none' },
    [mobile]: { padding: vars.spacing.padding.lg },
  },
});

export const overlayClosing = style({
  animation: `${fadeOut} ${vars.animation.duration.medium} ${vars.animation.easing.easeIn} forwards`,
  '@media': { [reducedMotion]: { animation: 'none' } },
});

// 모바일 하이브리드 — sm/md 는 중앙 유지, lg/xl(폼·다단계)은 모바일에서 풀스크린 테이크오버.
// 분기 기준은 엄지 도달권이 아니라 콘텐츠 분량 (docs/review/mobile-modal-a11y-research-2026-07-17.md)
export const overlaySize = styleVariants({
  sm: {},
  md: {},
  lg: { '@media': { [mobile]: { padding: 0 } } },
  xl: { '@media': { [mobile]: { padding: 0 } } },
});

// ---------- Panel — Solid 두께 + 네온 외곽선 ----------
export const panel = style({
  position: 'relative',
  width: '100%',
  maxHeight: `calc(100dvh - 48px - ${keyboardInset})`,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: vars.spacing.borderRadius.lg,
  // 다크 글래스(L1) + cyan tint film (header/footer 사이 중앙대만)
  background: `
    linear-gradient(180deg, transparent 0%, ${vars.bright.primary.glow} 50%, transparent 100%),
    ${vars.surface.liquidGlass.innerSurface.backgroundL1}
  `,
  backdropFilter: vars.surface.liquidGlass.backdropFilter.strong,
  WebkitBackdropFilter: vars.surface.liquidGlass.backdropFilter.strong,
  border: `1px solid ${vars.bright.primary.default}`,
  boxShadow: `
    /* 네온 외곽 글로우 */
    0 0 0 1px ${vars.bright.primary.glow},
    0 0 24px ${vars.bright.primary.glow},
    0 0 60px ${vars.bright.primary.glow},

    /* edge highlight */
    inset 0 1px 0 ${vars.bright.primary.glow},
    inset 1px 0 0 ${vars.opacityScale.whiteOverlay.subtle},
    inset 0 -1px 0 ${vars.opacityScale.blackOverlay.medium},
    inset -1px 0 0 ${vars.opacityScale.blackOverlay.light},

    /* Solid 두께 chain */
    0 1px 0 ${vars.bright.primary.glow},
    0 2px 0 ${vars.opacityScale.blackOverlay.strong},
    0 3px 0 ${vars.opacityScale.blackOverlay.strong},
    0 4px 0 ${vars.opacityScale.blackOverlay.strong},
    0 5px 0 ${vars.opacityScale.blackOverlay.firm},
    0 6px 0 ${vars.opacityScale.blackOverlay.firm},
    0 8px 0 ${vars.opacityScale.blackOverlay.prominent},
    0 10px 0 ${vars.opacityScale.blackOverlay.prominent},

    /* ground shadow */
    24px 36px 60px -8px ${vars.opacityScale.blackOverlay.veryStrong},
    12px 18px 30px -4px ${vars.opacityScale.blackOverlay.heavy}
  `,
  overflow: 'hidden',
  willChange: 'transform, opacity',
  animation: `${riseIn} ${vars.animation.duration.slow} ${vars.animation.easing.spring} forwards, ${neonPulse} 4s ease-in-out 560ms infinite`,
  '@media': {
    [reducedMotion]: { animation: 'none', transform: REST },
    [reducedTransparency]: {
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      background: vars.surface.card.default,
    },
  },
});

const panelFullscreenMobile = {
  '@media': {
    [mobile]: {
      maxWidth: '100%',
      height: `calc(100dvh - ${keyboardInset})`,
      maxHeight: `calc(100dvh - ${keyboardInset})`,
      borderRadius: 0,
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
    },
  },
} as const;

export const panelSize = styleVariants({
  sm: { maxWidth: '420px' },
  md: { maxWidth: '560px' },
  lg: { maxWidth: '720px', ...panelFullscreenMobile },
  xl: { maxWidth: '880px', ...panelFullscreenMobile },
});

// 스캔라인 오버레이
globalStyle(`${panel}::before`, {
  content: '""',
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  background: `repeating-linear-gradient(
    0deg,
    transparent 0,
    transparent 2px,
    ${vars.opacityScale.whiteOverlay.subtle} 2px,
    ${vars.opacityScale.whiteOverlay.subtle} 3px
  )`,
  opacity: 0.4,
  pointerEvents: 'none',
  mixBlendMode: 'overlay',
  zIndex: 4,
});

// directional lighting
globalStyle(`${panel}::after`, {
  content: '""',
  position: 'absolute',
  inset: 0,
  borderRadius: 'inherit',
  background: `linear-gradient(165deg,
    ${vars.bright.primary.glow} 0%,
    transparent 30%,
    transparent 70%,
    ${vars.opacityScale.blackOverlay.medium} 100%
  )`,
  pointerEvents: 'none',
  mixBlendMode: 'overlay',
  opacity: 0.6,
  zIndex: 5,
});

export const panelClosing = style({
  animation: `${riseOut} ${vars.animation.duration.medium} ${vars.animation.easing.easeIn} forwards`,
  '@media': { [reducedMotion]: { animation: 'none' } },
});

// ---------- Corner brackets (음영 입체화) ----------
export const cornerFrame = style({
  position: 'absolute',
  inset: vars.spacing.padding.sm,
  pointerEvents: 'none',
  zIndex: 7,
});

const cornerBase = {
  position: 'absolute',
  width: 16,
  height: 16,
  borderColor: vars.bright.primary.default,
  borderStyle: 'solid',
  borderWidth: 0,
  boxShadow: `
    0 0 6px ${vars.bright.primary.glow},
    0 0 14px ${vars.bright.primary.glow},
    0 1px 2px ${vars.opacityScale.blackOverlay.veryStrong},
    inset 0 0 4px ${vars.bright.primary.glow}
  `,
  filter: `drop-shadow(0 0 4px ${vars.bright.primary.glow})`,
} as const;

export const cornerTL = style({
  ...cornerBase,
  top: 0,
  left: 0,
  borderTopWidth: 2,
  borderLeftWidth: 2,
  borderTopLeftRadius: vars.spacing.borderRadius.sm,
});
export const cornerTR = style({
  ...cornerBase,
  top: 0,
  right: 0,
  borderTopWidth: 2,
  borderRightWidth: 2,
  borderTopRightRadius: vars.spacing.borderRadius.sm,
});
export const cornerBL = style({
  ...cornerBase,
  bottom: 0,
  left: 0,
  borderBottomWidth: 2,
  borderLeftWidth: 2,
  borderBottomLeftRadius: vars.spacing.borderRadius.sm,
});
export const cornerBR = style({
  ...cornerBase,
  bottom: 0,
  right: 0,
  borderBottomWidth: 2,
  borderRightWidth: 2,
  borderBottomRightRadius: vars.spacing.borderRadius.sm,
});

// ---------- Header — HUD ----------
export const header = style({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${vars.spacing.padding.lg} ${vars.spacing.padding.xl}`,
  borderBottom: `1px solid ${vars.bright.primary.dark}`,
  background: `linear-gradient(180deg, ${vars.bright.primary.glow} 0%, transparent 100%)`,
  position: 'relative',
  zIndex: 6,
  '@media': {
    [mobile]: {
      padding: `${vars.spacing.padding.md} ${vars.spacing.padding.lg}`,
    },
  },
});

export const titleRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.spacing.gap.sm,
  fontFamily: vars.typography.fontFamily.mono,
  minWidth: 0,
});

export const indicator = style({
  fontSize: vars.typography.fontSize['2xs'],
  color: vars.bright.primary.default,
  textShadow: `0 0 8px ${vars.bright.primary.glow}, 0 0 16px ${vars.bright.primary.glow}`,
  animation: `${hudBlink} 1.6s steps(1, end) infinite`,
  flexShrink: 0,
  '@media': { [reducedMotion]: { animation: 'none' } },
});

export const tag = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize['2xs'],
  letterSpacing: vars.typography.letterSpacing.display,
  color: vars.bright.primary.light,
  textShadow: `0 0 6px ${vars.bright.primary.glow}`,
  padding: `${vars.spacing.padding['2xs']} ${vars.spacing.padding.xs}`,
  border: `1px solid ${vars.bright.primary.dark}`,
  borderRadius: vars.spacing.borderRadius.xs,
  textTransform: 'uppercase',
  flexShrink: 0,
});

export const title = style({
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.md,
  fontWeight: vars.typography.fontWeight.bold,
  color: vars.bright.primary.light,
  letterSpacing: vars.typography.letterSpacing.label,
  textShadow: `0 0 8px ${vars.bright.primary.glow}, 0 0 16px ${vars.bright.primary.glow}`,
  margin: 0,
  textTransform: 'uppercase',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const closeButton = style({
  background: 'none',
  border: `1px solid ${vars.bright.primary.dark}`,
  color: vars.bright.primary.light,
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize['2xs'],
  cursor: 'pointer',
  padding: `${vars.spacing.padding.xs} ${vars.spacing.padding.sm}`,
  borderRadius: vars.spacing.borderRadius.xs,
  lineHeight: vars.typography.lineHeight.none,
  textShadow: `0 0 6px ${vars.bright.primary.glow}`,
  transition: `all ${vars.animation.duration.fast}`,
  flexShrink: 0,
  '@media': {
    // WCAG 2.5.5/2.5.8 — 모바일 주요 액션 44px 터치 타깃 (연구 보고서 시사점 ④)
    [mobile]: { minHeight: '44px', minWidth: '44px' },
  },
  selectors: {
    '&:hover': {
      background: vars.bright.primary.glow,
      borderColor: vars.bright.primary.default,
    },
    '&:focus-visible': {
      outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
      outlineOffset: vars.focus.ring.offset,
    },
  },
});

// ---------- Body / Footer ----------
export const body = style({
  flex: 1,
  padding: `${vars.spacing.padding.lg} ${vars.spacing.padding.xl}`,
  overflowY: 'auto',
  position: 'relative',
  zIndex: 6,
  '@media': {
    [mobile]: { padding: vars.spacing.padding.lg },
  },
});

export const footer = style({
  display: 'flex',
  gap: vars.spacing.gap.md,
  justifyContent: 'flex-end',
  padding: `${vars.spacing.padding.md} ${vars.spacing.padding.xl} ${vars.spacing.padding.lg}`,
  borderTop: `1px solid ${vars.bright.primary.dark}`,
  background: `linear-gradient(0deg, ${vars.bright.primary.glow} 0%, transparent 100%)`,
  position: 'relative',
  zIndex: 6,
  '@media': {
    [mobile]: { padding: `${vars.spacing.padding.sm} ${vars.spacing.padding.lg}` },
  },
});

// ============================================================================
// Footer cascade — atoms/Button 또는 native <button> 자동 3D 톤
// 모달 footer 안 button 인스턴스만 매칭. atoms/Button.css 자체는 안 바뀜.
// 모달 밖 atoms/Button 사용처는 평면 톤 그대로 유지.
// ============================================================================

// flat × warning 은 Button 아톰의 경고 채움·inverse 글자색을 그대로 쓴다.
const cascadeBase = `${footer} button:not(:where([data-variant="flat"][data-tone="warning"]))`;

globalStyle(cascadeBase, {
  fontFamily: vars.typography.fontFamily.mono,
  fontSize: vars.typography.fontSize.xs,
  fontWeight: vars.typography.fontWeight.bold,
  letterSpacing: vars.typography.letterSpacing.display,
  textTransform: 'uppercase',
  padding: `${vars.spacing.padding.sm} ${vars.spacing.padding.lg}`,
  borderRadius: vars.spacing.borderRadius.sm,
  cursor: 'pointer',
  position: 'relative',
  border: `1px solid ${vars.bright.primary.light}`,
  // primary: 솔리드 azure — secondary outline 과 명확히 구분
  background: `linear-gradient(180deg, ${vars.bright.primary.default} 0%, ${vars.bright.primary.dark} 100%)`,
  color: vars.typography.textColor.primary,
  textShadow: `0 1px 1px ${vars.opacityScale.blackOverlay.veryStrong}`,
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',
  animation: 'none',
  transition: `transform ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, box-shadow ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, background ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}, border-color ${vars.animation.duration.fast} ${vars.animation.easing.easeOut}`,
  willChange: 'transform, box-shadow',
  '@media': {
    [mobile]: { minHeight: '44px' },
  },
  boxShadow: `
    inset 0 1px 0 ${vars.opacityScale.whiteOverlay.heavy},
    inset 1px 0 0 ${vars.opacityScale.whiteOverlay.bold},
    inset 0 -1px 0 ${vars.opacityScale.blackOverlay.strong},
    0 1px 0 ${vars.opacityScale.blackOverlay.strong},
    0 2px 0 ${vars.opacityScale.blackOverlay.strong},
    0 3px 0 ${vars.opacityScale.blackOverlay.strong},
    0 4px 0 ${vars.opacityScale.blackOverlay.firm},
    0 5px 0 ${vars.opacityScale.blackOverlay.firm},
    0 0 10px ${vars.bright.primary.glow},
    0 0 20px ${vars.bright.primary.glow},
    0 8px 14px -2px ${vars.opacityScale.blackOverlay.heavy}
  `,
});

globalStyle(`${cascadeBase}:hover:not(:disabled)`, {
  transform: 'translateY(1px)',
  boxShadow: `
    inset 0 1px 0 ${vars.opacityScale.whiteOverlay.heavy},
    inset 1px 0 0 ${vars.opacityScale.whiteOverlay.bold},
    inset 0 -1px 0 ${vars.opacityScale.blackOverlay.strong},
    0 1px 0 ${vars.opacityScale.blackOverlay.strong},
    0 2px 0 ${vars.opacityScale.blackOverlay.strong},
    0 3px 0 ${vars.opacityScale.blackOverlay.strong},
    0 4px 0 ${vars.opacityScale.blackOverlay.firm},
    0 0 14px ${vars.bright.primary.glow},
    0 0 28px ${vars.bright.primary.glow},
    0 6px 10px -2px ${vars.opacityScale.blackOverlay.heavy}
  `,
});

globalStyle(`${cascadeBase}:active:not(:disabled)`, {
  transform: 'translateY(4px)',
  boxShadow: `
    inset 0 1px 0 ${vars.opacityScale.whiteOverlay.intense},
    inset 0 -1px 0 ${vars.opacityScale.blackOverlay.strong},
    0 1px 0 ${vars.opacityScale.blackOverlay.strong},
    0 0 8px ${vars.bright.primary.glow},
    0 2px 4px -1px ${vars.opacityScale.blackOverlay.heavy}
  `,
});

globalStyle(`${cascadeBase}:focus-visible`, {
  outline: `${vars.focus.ring.width} ${vars.focus.ring.style} ${vars.focus.ring.color}`,
  outlineOffset: vars.focus.ring.offset,
});

// secondary: outlined 3D + cyan glow ring (저장 등)
const secondarySel = `${cascadeBase}[data-variant="secondary"]`;

globalStyle(secondarySel, {
  background: 'transparent',
  borderColor: vars.bright.primary.dark,
  color: vars.bright.primary.light,
  boxShadow: `
    inset 0 1px 0 ${vars.opacityScale.whiteOverlay.bold},
    inset 0 -1px 0 ${vars.opacityScale.blackOverlay.mediumStrong},
    0 1px 0 ${vars.opacityScale.blackOverlay.mediumStrong},
    0 2px 0 ${vars.opacityScale.blackOverlay.mediumStrong},
    0 3px 0 ${vars.opacityScale.blackOverlay.strong},
    0 4px 0 ${vars.opacityScale.blackOverlay.strong},
    0 0 6px ${vars.bright.primary.glow},
    0 6px 10px -2px ${vars.opacityScale.blackOverlay.heavy}
  `,
});

globalStyle(`${secondarySel}:hover:not(:disabled)`, {
  background: vars.bright.primary.glow,
  borderColor: vars.bright.primary.default,
  boxShadow: `
    inset 0 1px 0 ${vars.opacityScale.whiteOverlay.strong},
    inset 0 -1px 0 ${vars.opacityScale.blackOverlay.mediumStrong},
    0 1px 0 ${vars.opacityScale.blackOverlay.mediumStrong},
    0 2px 0 ${vars.opacityScale.blackOverlay.mediumStrong},
    0 3px 0 ${vars.opacityScale.blackOverlay.strong},
    0 0 12px ${vars.bright.primary.glow},
    0 0 22px ${vars.bright.primary.glow},
    0 5px 8px -2px ${vars.opacityScale.blackOverlay.heavy}
  `,
});

// ghost: flat 텍스트 버튼 (취소 등) — 위계상 가장 낮음
const ghostSel = `${cascadeBase}[data-variant="ghost"]`;

globalStyle(ghostSel, {
  background: 'transparent',
  borderColor: 'transparent',
  color: vars.typography.textColor.tertiary,
  textShadow: 'none',
  boxShadow: 'none',
});

globalStyle(`${ghostSel}:hover:not(:disabled)`, {
  background: vars.opacityScale.whiteOverlay.subtle,
  color: vars.bright.primary.light,
  transform: 'none',
  boxShadow: 'none',
});

globalStyle(`${ghostSel}:active:not(:disabled)`, {
  transform: 'none',
  boxShadow: 'none',
});

const dangerSel = `${cascadeBase}[data-variant="danger"]`;
// vars.bright.glow.red 는 box-shadow string (color 아님) — status.error + alpha hex 로 교체
// primary 와 raised 양감 일치 (top+left inset 하이라이트, 그라디언트 어두워짐)
globalStyle(dangerSel, {
  background: `linear-gradient(180deg, transparent 0%, ${vars.opacityScale.blackOverlay.medium} 100%), ${vars.bright.status.error}`,
  borderColor: vars.bright.status.error,
  color: vars.typography.textColor.primary,
  textShadow: `0 1px 1px ${vars.opacityScale.blackOverlay.veryStrong}`,
  boxShadow: `
    inset 0 1px 0 ${vars.opacityScale.whiteOverlay.heavy},
    inset 1px 0 0 ${vars.opacityScale.whiteOverlay.bold},
    inset 0 -1px 0 ${vars.opacityScale.blackOverlay.strong},
    0 1px 0 ${vars.opacityScale.blackOverlay.strong},
    0 2px 0 ${vars.opacityScale.blackOverlay.strong},
    0 3px 0 ${vars.opacityScale.blackOverlay.strong},
    0 4px 0 ${vars.opacityScale.blackOverlay.firm},
    0 5px 0 ${vars.opacityScale.blackOverlay.firm},
    0 0 10px ${vars.opacityScale.errorRed.intense},
    0 0 20px ${vars.opacityScale.errorRed.intense},
    0 8px 14px -2px ${vars.opacityScale.blackOverlay.heavy}
  `,
});
